import React, { useState, useEffect } from "react";
import { db, storage } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import {
  Add, Edit2, Trash, Gallery, Calendar, Profile2User, DocumentUpload,
  Location, ArrowDown2, SearchNormal1, Setting4, Wifi, BatteryFull,
} from "iconsax-reactjs";
import "./BannerPanel.css";
import { formatExpiryDate } from "../utils/dateFormatter";

const PRESET_BANNER_IMAGES = [
  "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=600&q=80",
  "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=600&q=80",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=600&q=80",
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=600&q=80"
];

// One carousel slide, drawn the way the app draws it (PromoCarousel.tsx):
// gradient card, heading and subheading on the left, image in a curved
// window on the right. Used by the list cards and the editor preview.
function BannerArt({ heading, subheading, desc, image, expiryLabel }) {
  return (
    <div className="bp-art">
      <div className="bp-art-text">
        <span className="bp-art-title">{heading}</span>
        <span className="bp-art-title">{subheading}</span>
        {desc && <span className="bp-art-desc">{desc}</span>}
        {expiryLabel && <span className="bp-art-expiry">{expiryLabel}</span>}
      </div>
      <div className="bp-art-media">
        <span className="bp-art-placeholder" aria-hidden="true">
          <Gallery size={20} variant="Linear" />
        </span>
        {image && (
          <img
            src={image}
            alt=""
            onError={(e) => { e.currentTarget.hidden = true; }}
          />
        )}
      </div>
    </div>
  );
}

export default function BannerPanel() {
  const [bannersList, setBannersList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [heading, setHeading] = useState("");
  const [subheading, setSubheading] = useState("");
  const [desc, setDesc] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [hasExpiry, setHasExpiry] = useState(false);
  const [expiryDate, setExpiryDate] = useState("");
  const [showOnTenant, setShowOnTenant] = useState(true);
  const [showOnDealer, setShowOnDealer] = useState(true);

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // The editor (form + app preview) opens in a modal from "Add banner" or a
  // card's Edit button.
  const [editorOpen, setEditorOpen] = useState(false);

  // 1. Fetch & Listen to Banners collection with Migration logic
  useEffect(() => {
    const bannersCol = collection(db, "banners");
    const unsubscribe = onSnapshot(bannersCol, async (snap) => {
      const list = [];
      snap.forEach((docSnap) => {
        list.push({ id: docSnap.id, ...docSnap.data() });
      });

      // Auto-migration: if collection is empty, check old config doc
      if (list.length === 0) {
        try {
          const oldConfigRef = doc(db, "config", "topBanner");
          const oldSnap = await getDoc(oldConfigRef);
          if (oldSnap.exists()) {
            const oldData = oldSnap.data();
            const newDocRef = doc(collection(db, "banners"));
            await setDoc(newDocRef, {
              heading: oldData.heading || "GET HOME LOAN",
              subheading: oldData.subheading || "STARTING 7.6%*",
              desc: oldData.desc || "*ZERO PROCESSING FEE",
              image: oldData.image || PRESET_BANNER_IMAGES[0],
              hasExpiry: false,
              expiryDate: null,
              updatedAt: serverTimestamp()
            });
            console.log("Auto-migrated old config to new banners collection!");
          }
        } catch (err) {
          console.error("Migration check failed:", err);
        }
      }

      // Sort by updatedAt desc
      list.sort((a, b) => {
        const t1 = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : 0;
        const t2 = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : 0;
        return t2 - t1;
      });

      setBannersList(list);
      setLoading(false);
    }, (err) => {
      console.error("Error listening to banners:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Escape closes the editor, unless an upload or save is in flight.
  useEffect(() => {
    if (!editorOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !isSaving) {
        setEditorOpen(false);
        setSelectedBanner(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [editorOpen, isSaving]);

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const resetForm = () => {
    setSelectedBanner(null);
    setHeading("");
    setSubheading("");
    setDesc("");
    setImageUrl("");
    setHasExpiry(false);
    setExpiryDate("");
    setShowOnTenant(true);
    setShowOnDealer(true);
    setSelectedFile(null);
    setUploadProgress(0);
    setEditorOpen(false);
  };

  const handleEditSelect = (banner) => {
    setSelectedBanner(banner);
    setHeading(banner.heading || "");
    setSubheading(banner.subheading || "");
    setDesc(banner.desc || "");
    setImageUrl(banner.image || "");
    setHasExpiry(banner.hasExpiry || false);
    setShowOnTenant(banner.showOnTenant !== false);
    setShowOnDealer(banner.showOnDealer !== false);

    // Parse expiry date into standard YYYY-MM-DD for date input
    if (banner.expiryDate) {
      const date = banner.expiryDate.toDate ? banner.expiryDate.toDate() : new Date(banner.expiryDate);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      setExpiryDate(`${year}-${month}-${day}`);
    } else {
      setExpiryDate("");
    }
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const openNewEditor = () => {
    resetForm();
    setEditorOpen(true);
  };

  const openEditEditor = (banner) => {
    handleEditSelect(banner);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (isSaving) return;
    resetForm();
  };

  const handleToggleEnable = async (banner) => {
    try {
      const bannerDocRef = doc(db, "banners", banner.id);
      const nextEnabledState = banner.isEnabled === false;
      await setDoc(bannerDocRef, { isEnabled: nextEnabledState }, { merge: true });
    } catch (err) {
      console.error("Failed to toggle enable state:", err);
      alert("Error toggling banner status.");
    }
  };

  const handleDelete = async (bannerId) => {
    if (window.confirm("Are you sure you want to delete this banner?")) {
      try {
        await deleteDoc(doc(db, "banners", bannerId));
        alert("Banner deleted successfully!");
        if (selectedBanner?.id === bannerId) {
          resetForm();
        }
      } catch (err) {
        console.error("Delete banner failed:", err);
        alert("Failed to delete banner.");
      }
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      let finalImageUrl = imageUrl;
      const bannerDocRef = selectedBanner
        ? doc(db, "banners", selectedBanner.id)
        : doc(collection(db, "banners"));

      // 1. Upload new image if selected
      if (selectedFile) {
        setUploadProgress(5);
        const storageRef = ref(storage, `banners/${bannerDocRef.id}_${Date.now()}_${selectedFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, selectedFile);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(Math.round(progress));
            },
            (error) => {
              console.error("Banner upload error:", error);
              reject(error);
            },
            async () => {
              finalImageUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            }
          );
        });
      }

      // 2. Parse expiry date (set it to 23:59:59 of that day)
      let expiryTime = null;
      if (hasExpiry && expiryDate) {
        expiryTime = new Date(expiryDate + "T23:59:59");
      }

      // 3. Save config to Firestore doc
      const savePayload = {
        heading,
        subheading,
        desc,
        image: finalImageUrl || PRESET_BANNER_IMAGES[0],
        hasExpiry,
        expiryDate: expiryTime,
        showOnTenant,
        showOnDealer,
        updatedAt: serverTimestamp()
      };

      if (!selectedBanner) {
        savePayload.isEnabled = true;
      }

      await setDoc(bannerDocRef, savePayload, { merge: true });

      alert(selectedBanner ? "Banner updated successfully!" : "New Banner created successfully!");
      resetForm();
    } catch (err) {
      console.error("Failed to save banner config:", err);
      alert("Error saving banner configuration.");
    } finally {
      setIsSaving(false);
      setUploadProgress(0);
    }
  };

  const checkIsExpired = (banner) => {
    if (!banner.hasExpiry || !banner.expiryDate) return false;
    const expiry = banner.expiryDate.toDate ? banner.expiryDate.toDate() : new Date(banner.expiryDate);
    return expiry <= new Date();
  };

  const liveCount = bannersList.filter((b) => b.isEnabled !== false && !checkIsExpired(b)).length;
  const previewImage = selectedFile ? URL.createObjectURL(selectedFile) : (imageUrl || PRESET_BANNER_IMAGES[0]);

  return (
    <div className="bp-panel">
      <div className="toolbar bp-toolbar">
        <p className="bp-intro">
          {!loading && bannersList.length > 0 && (
            <strong>{liveCount} of {bannersList.length} live. </strong>
          )}
          Banners rotate on the app home screen and hide automatically after their expiry date.
        </p>
        <span className="toolbar-spacer" />
        <button type="button" className="btn-primary" onClick={openNewEditor}>
          <Add size={18} variant="Linear" />
          Add banner
        </button>
      </div>

      {loading ? (
        <ul className="bp-grid" aria-busy="true" aria-label="Loading banners">
          {[0, 1, 2].map((i) => (
            <li key={i} className="card bp-card">
              <div className="bp-card-media">
                <div className="skeleton bp-skel-art" />
              </div>
              <div className="bp-card-body">
                <div className="skeleton bp-skel-line" />
                <div className="skeleton bp-skel-line short" />
              </div>
              <div className="bp-card-actions" />
            </li>
          ))}
        </ul>
      ) : bannersList.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <span className="empty-state-icon" aria-hidden="true">
              <Gallery size={22} variant="Linear" />
            </span>
            <h3>No banners yet</h3>
            <p>Banners you add appear in the carousel at the top of the app home screen, for tenants, dealers or both.</p>
          </div>
        </div>
      ) : (
        <ul className="bp-grid">
          {bannersList.map((banner) => {
            const expired = checkIsExpired(banner);
            const disabled = banner.isEnabled === false;
            const statusLabel = disabled ? "Disabled" : (expired ? "Expired" : "Active");
            const statusClass = disabled ? "" : (expired ? "chip-warning" : "chip-success");
            const audience = [
              banner.showOnTenant !== false && "Tenants",
              banner.showOnDealer !== false && "Dealers"
            ].filter(Boolean).join(", ") || "None";

            return (
              <li
                key={banner.id}
                className={`card bp-card ${selectedBanner?.id === banner.id ? "is-selected" : ""} ${disabled || expired ? "is-off" : ""}`}
              >
                <div className="bp-card-media">
                  <BannerArt
                    heading={banner.heading}
                    subheading={banner.subheading}
                    desc={banner.desc}
                    image={banner.image}
                  />
                </div>

                <div className="bp-card-body">
                  <div className="bp-card-meta">
                    <span className={`chip chip-dot ${statusClass}`}>{statusLabel}</span>
                    <span className="bp-meta" title="Visible to">
                      <Profile2User size={16} variant="Linear" aria-hidden="true" />
                      <span className="sr-only">Visible to: </span>
                      {audience}
                    </span>
                  </div>
                  <span className={`bp-meta ${expired ? "is-expired" : ""}`}>
                    <Calendar size={16} variant="Linear" aria-hidden="true" />
                    {banner.hasExpiry && banner.expiryDate
                      ? `${expired ? "Expired" : "Expires"} ${formatExpiryDate(banner.expiryDate)}`
                      : "No expiry date"}
                  </span>
                </div>

                <div className="bp-card-actions">
                  <label className="bp-switch">
                    <input
                      type="checkbox"
                      role="switch"
                      checked={!disabled}
                      onChange={() => handleToggleEnable(banner)}
                    />
                    <span className="bp-switch-track" aria-hidden="true" />
                    <span>Enabled</span>
                  </label>
                  <span className="toolbar-spacer" />
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => openEditEditor(banner)}
                  >
                    <Edit2 size={16} variant="Linear" />
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn-icon bp-delete"
                    onClick={() => handleDelete(banner.id)}
                    aria-label={`Delete banner ${banner.heading || ""}`.trim()}
                    title="Delete banner"
                  >
                    <Trash size={18} variant="Linear" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editorOpen && (
        <div className="modal-overlay">
          <form
            onSubmit={handleSave}
            className="modal bp-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bp-editor-title"
          >
            <div className="modal-header">
              <h2 id="bp-editor-title">{selectedBanner ? "Edit banner" : "Add banner"}</h2>
              <button
                type="button"
                className="btn-icon"
                onClick={closeEditor}
                disabled={isSaving}
                aria-label="Close"
              >
                <Add size={22} variant="Linear" className="bp-close-icon" />
              </button>
            </div>

            <div className="modal-body">
              <div className="bp-editor">
                <div className="bp-fields">
                  <div className="field">
                    <label className="field-label" htmlFor="bp-heading">Heading</label>
                    <input
                      id="bp-heading"
                      className="input"
                      type="text"
                      placeholder="e.g. GET HOME LOAN"
                      value={heading}
                      onChange={(e) => setHeading(e.target.value.toUpperCase())}
                      required
                    />
                  </div>

                  <div className="field">
                    <label className="field-label" htmlFor="bp-subheading">Subheading</label>
                    <input
                      id="bp-subheading"
                      className="input"
                      type="text"
                      placeholder="e.g. STARTING 7.6%*"
                      value={subheading}
                      onChange={(e) => setSubheading(e.target.value.toUpperCase())}
                      required
                    />
                  </div>

                  <div className="field bp-span-2">
                    <label className="field-label" htmlFor="bp-desc">Description</label>
                    <input
                      id="bp-desc"
                      className="input"
                      type="text"
                      placeholder="e.g. *ZERO PROCESSING FEE"
                      value={desc}
                      onChange={(e) => setDesc(e.target.value.toUpperCase())}
                    />
                    <span className="field-hint">Optional. Small print under the subheading.</span>
                  </div>

                  <div className="field bp-span-2">
                    <span className="field-label" id="bp-image-label">Image</span>
                    <div className="bp-image-row" role="group" aria-labelledby="bp-image-label">
                      <label className="btn-secondary bp-upload">
                        <DocumentUpload size={18} variant="Linear" />
                        Upload image
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="sr-only"
                        />
                      </label>
                      <span className="bp-image-or">or pick a preset</span>
                      <div className="bp-presets">
                        {PRESET_BANNER_IMAGES.map((imgUrl, idx) => {
                          const active = imageUrl === imgUrl && !selectedFile;
                          return (
                            <button
                              key={idx}
                              type="button"
                              className="bp-preset"
                              aria-pressed={active}
                              aria-label={`Preset image ${idx + 1}`}
                              onClick={() => {
                                setSelectedFile(null);
                                setImageUrl(imgUrl);
                              }}
                            >
                              <img src={imgUrl} alt="" />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {selectedFile && (
                      <span className="field-hint bp-file-name">Selected: {selectedFile.name}</span>
                    )}
                  </div>

                  <fieldset className="field bp-span-2 bp-fieldset">
                    <legend className="field-label">Show to</legend>
                    <div className="bp-checks">
                      <label className="bp-check">
                        <input
                          type="checkbox"
                          checked={showOnTenant}
                          onChange={(e) => setShowOnTenant(e.target.checked)}
                        />
                        Tenants
                      </label>
                      <label className="bp-check">
                        <input
                          type="checkbox"
                          checked={showOnDealer}
                          onChange={(e) => setShowOnDealer(e.target.checked)}
                        />
                        Dealers
                      </label>
                    </div>
                  </fieldset>

                  <div className="field bp-span-2">
                    <label className="bp-switch">
                      <input
                        type="checkbox"
                        role="switch"
                        checked={hasExpiry}
                        onChange={(e) => setHasExpiry(e.target.checked)}
                      />
                      <span className="bp-switch-track" aria-hidden="true" />
                      <span>Expires on a date</span>
                    </label>
                    {hasExpiry ? (
                      <div className="bp-expiry">
                        <label className="sr-only" htmlFor="bp-expiry-date">Expiry date</label>
                        <input
                          id="bp-expiry-date"
                          className="input"
                          type="date"
                          value={expiryDate}
                          onChange={(e) => setExpiryDate(e.target.value)}
                          required={hasExpiry}
                        />
                        <span className="field-hint">The banner hides at the end of this day.</span>
                      </div>
                    ) : (
                      <span className="field-hint">Off: the banner stays up until you disable or delete it.</span>
                    )}
                  </div>
                </div>

                <div className="bp-preview">
                  <div className="bp-preview-head">
                    <span className="field-label">App preview</span>
                    <span className="field-hint">Home screen</span>
                  </div>

                  <div className="bp-phone" aria-hidden="true">
                    <div className="bp-phone-screen">
                      <div className="bp-phone-status">
                        <span>9:41</span>
                        <span className="bp-phone-status-icons">
                          <Wifi size={14} variant="Bold" />
                          <BatteryFull size={16} variant="Linear" />
                        </span>
                      </div>

                      <div className="bp-app">
                        <div className="bp-app-header">
                          <div className="bp-app-greeting">
                            <span className="bp-app-name">Hi, Ravi Srivastava</span>
                            <span className="bp-app-location">
                              <Location size={12} variant="Linear" />
                              HSR Layout, Bengaluru
                              <ArrowDown2 size={12} variant="Linear" />
                            </span>
                          </div>
                          <div className="bp-app-toggle">
                            <span className="is-active">Resi</span>
                            <span>Comm</span>
                          </div>
                        </div>

                        <div className="bp-app-search">
                          <SearchNormal1 size={14} variant="Linear" />
                          <span>Search address, city...</span>
                          <span className="bp-app-filter">
                            <Setting4 size={12} variant="Linear" />
                          </span>
                        </div>

                        <BannerArt
                          heading={heading || "GET HOME LOAN"}
                          subheading={subheading || "STARTING 7.5%*"}
                          desc={desc || "*ZERO PROCESSING FEE"}
                          image={previewImage}
                          expiryLabel={hasExpiry && expiryDate ? `*Expires ${formatExpiryDate(expiryDate)}` : ""}
                        />

                        <div className="bp-app-dots">
                          <i className="is-active" />
                          <i />
                          <i />
                        </div>

                        <div className="bp-app-pills">
                          <span className="is-active">Buy</span>
                          <span>Sell</span>
                          <span>Rent</span>
                        </div>

                        <div className="bp-app-section">
                          <span className="bp-app-section-title">Near your location</span>
                          <span className="bp-app-see-all">See all</span>
                        </div>

                        <div className="bp-app-listing">
                          <span className="bp-app-listing-img" />
                          <span className="bp-app-listing-lines">
                            <i />
                            <i className="short" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              {uploadProgress > 0 && (
                <div className="bp-progress" role="status">
                  <span className="bp-progress-track">
                    <span className="bp-progress-fill" style={{ width: `${uploadProgress}%` }} />
                  </span>
                  <span>Uploading image {uploadProgress}%</span>
                </div>
              )}
              <button type="button" className="btn-secondary" onClick={closeEditor} disabled={isSaving}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={isSaving}>
                {isSaving && <span className="btn-spinner" aria-hidden="true" />}
                {isSaving ? "Saving..." : selectedBanner ? "Save changes" : "Create banner"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
